import { Autocomplete, Chip, TextField } from '@mui/material';
import { cn } from 'cn';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { isFieldRequired, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type P = {
  readonly hint?: string;
  // Shown when the user tries to commit a value that fails `validateValue`.
  readonly invalidValueMessage?: string;
  readonly isDisabled?: boolean;
  readonly label?: string;
  // Rendered at the right end of the label row.
  readonly labelAction?: ReactNode;
  readonly placeholder?: string;
  // When provided, values that fail validation are rejected on Enter and on
  // blur instead of being added as chips.
  readonly validateValue?: (value: string) => boolean;
} & ComponentStyling;

function getSchemaErrorMessage(errors: (string | { message?: string })[]) {
  return unique(
    errors
      // The pending-input entry is a plain string; its display is gated on
      // focus instead.
      .flatMap((error) =>
        typeof error === 'string' ? [] : (error.message ?? []),
      ),
  ).join(', ');
}

function getErrorMessage({
  fieldErrors,
  inputError,
  pendingValueError,
}: {
  fieldErrors: string;
  inputError: string;
  pendingValueError: string;
}) {
  return inputError || pendingValueError || fieldErrors;
}

/**
 * Free-text chip input: type a value and press Enter, or leave the field, to
 * collapse it into a removable chip. Operates on a `string[]` form field.
 * Invalid values are rejected (the text stays in the input with an inline
 * message) rather than being added and flagged afterwards.
 *
 * Uncommitted input text keeps the form unsubmittable so it can never be lost
 * silently on save; the message telling the user to commit or clear it is only
 * shown once the input loses focus.
 */
export const ChipInputField = ({
  className,
  hint,
  invalidValueMessage,
  isDisabled = false,
  label,
  labelAction,
  placeholder,
  style,
  validateValue,
}: P) => {
  const { t } = useTranslation('forms');
  const field = useFieldContext<string[]>();
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const pendingValue = inputValue.trim();
  const pendingValueError = t(($) => $.chipInputField.pendingValueError);
  const { onSubmit: submitError } = field.state.meta.errorMap as {
    onSubmit?: string;
  };

  // Sync the pending (uncommitted) input text into the field's submit error
  // map so `form.handleSubmit` is blocked while text has not been turned into
  // a chip. Field validation runs clear stale submit errors, so this re-asserts
  // after every render instead of setting it once in the event handlers. Only
  // its own message is ever cleared, so a form validator's submit error stays.
  useEffect(() => {
    if (pendingValue && submitError !== pendingValueError) {
      field.setErrorMap({ onSubmit: pendingValueError });
    } else if (!pendingValue && submitError === pendingValueError) {
      field.setErrorMap({ onSubmit: undefined });
    }
  }, [field, pendingValue, pendingValueError, submitError]);

  const fieldErrors = getSchemaErrorMessage(
    field.state.meta.errors as (string | { message?: string })[],
  );
  const shouldShowPendingValueError = Boolean(pendingValue) && !isInputFocused;
  const errorMessage = getErrorMessage({
    fieldErrors,
    inputError,
    pendingValueError: shouldShowPendingValueError ? pendingValueError : '',
  });
  const hasErrors = Boolean(errorMessage);

  const id = useId();
  const values = field.state.value ?? [];

  const addValue = (rawValue: string) => {
    const value = rawValue.trim();

    if (!value) {
      return;
    }

    if (validateValue && !validateValue(value)) {
      setInputError(invalidValueMessage ?? '');
      return;
    }

    field.handleChange(unique([...values, value]));
    setInputValue('');
    setInputError('');
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    // An Enter that confirms an IME composition is not a commit.
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) {
      return;
    }

    // Take over Enter so MUI never adds an unvalidated chip and the form does
    // not submit.
    event.preventDefault();
    event.stopPropagation();
    addValue(inputValue);
  };

  return (
    <div className={cn('relative pb-1.5', className)} style={style}>
      {label || labelAction ? (
        <div className="flex items-center justify-between gap-2">
          {label ? (
            <InputLabel hint={hint} htmlFor={id} isRequired={isRequired}>
              {label}
            </InputLabel>
          ) : null}
          {labelAction}
        </div>
      ) : null}
      <Autocomplete
        className="bg-white"
        disabled={isDisabled}
        id={id}
        inputValue={inputValue}
        options={[]}
        size="small"
        value={values}
        freeSolo
        multiple
        renderInput={(params) => (
          <TextField
            {...params}
            error={hasErrors}
            placeholder={placeholder}
            slotProps={{
              input: {
                ...params.InputProps,
                className: cn(params.InputProps.className, 'text-sm'),
              },
            }}
            onFocus={() => {
              setIsInputFocused(true);
            }}
            onKeyDown={handleKeyDown}
          />
        )}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => {
            const { key, ...tagProps } = getTagProps({ index });

            return (
              <Chip
                key={key}
                {...tagProps}
                className={cn(tagProps.className, 'text-sm')}
                label={tag}
                size="small"
              />
            );
          })
        }
        onBlur={() => {
          addValue(inputValue);
          setIsInputFocused(false);
          field.handleBlur();
        }}
        onChange={(_, newValues) => {
          // Fires for chip removal / clear; additions are handled on Enter.
          field.handleChange(
            unique(newValues.map((value) => value.trim()).filter(Boolean)),
          );
        }}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);

          if (inputError) {
            setInputError('');
          }
        }}
      />
      {hasErrors ? <ValidationError>{errorMessage}</ValidationError> : null}
    </div>
  );
};
